class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.requestId = 0;

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('input', () => {
      this.requestId++;
      this.predictiveSearchResults.innerHTML = '';
      this.removeAttribute('results');
      this.removeAttribute('open');
      // Give immediate feedback during the 300 ms debounce and the catalog request.
      if (this.getQuery()) this.setLiveRegionLoadingState();
      else this.removeAttribute('loading');
    });
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
      return;
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  aloraCanonicalTerm(term) {
    // Customers use both "collar" and "collares" for the same catalog items.
    return term.trim().replace(/\bcollares\b/gi, 'collar');
  }

  async getSearchResults(searchTerm) {
    const queryKey = searchTerm.toLocaleLowerCase().trim();
    const requestId = ++this.requestId;
    this.setLiveRegionLoadingState();
    const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      return;
    }

    const canonicalTerm = this.aloraCanonicalTerm(searchTerm);
    this.abortController.abort();
    this.abortController = new AbortController();
    const results = [];
    let nextPage = '1';
    let failed = false;

    try {
      // The full search page has all catalog matches. Predictive Search only
      // returns a small, broad sample and can omit exact title matches.
      for (let pagesRead = 0; nextPage && pagesRead < 4 && results.length < 8; pagesRead++) {
        const searchUrl = window.routes?.search_url || this.input.form.action;
        const url = `${searchUrl}?q=${encodeURIComponent(canonicalTerm)}&type=product&options%5Bprefix%5D=last&page=${nextPage}`;
        const response = await fetch(url, { signal: this.abortController.signal });
        if (!response.ok) throw new Error(`Search ${response.status}`);
        const page = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (!page.querySelector('.template-search')) throw new Error('Search page unavailable');
        page.querySelectorAll('.template-search #product-grid .product-grid > li').forEach((card) => {
          const link = card.querySelector('.card__heading a[href]');
          const title = link?.textContent?.trim();
          const normalized = (value) => value.toLocaleLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
          if (!title || !normalized(title).includes(normalized(canonicalTerm))) return;
          if (results.some((item) => item.querySelector('a')?.href === link.href)) return;
          const item = document.createElement('li');
          item.className = 'predictive-search__list-item';
          item.setAttribute('role', 'option');
          item.setAttribute('aria-selected', 'false');
          const anchor = document.createElement('a');
          anchor.href = link.href;
          anchor.className = 'predictive-search__item predictive-search__item--link-with-thumbnail link link--text';
          anchor.tabIndex = -1;
          const sourceImage = card.querySelector('.card__media img');
          if (sourceImage) {
            const img = document.createElement('img');
            img.className = 'predictive-search__image';
            img.src = sourceImage.getAttribute('src');
            img.alt = sourceImage.alt;
            anchor.append(img);
          }
          const content = document.createElement('div');
          content.className = 'predictive-search__item-content';
          const heading = document.createElement('p');
          heading.className = 'predictive-search__item-heading h5';
          heading.textContent = title;
          content.append(heading);
          const cardPrice = card.querySelector('.price-item--regular, .price-item--sale');
          if (cardPrice) {
            const price = document.createElement('span');
            price.className = 'alora-search-result-price';
            price.textContent = cardPrice.textContent.trim();
            content.append(price);
          }
          anchor.append(content);
          item.append(anchor);
          results.push(item);
        });
        nextPage = page.querySelector('.template-search #product-grid')?.dataset.aloraNextPage || '';
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      failed = true;
    }

    if (requestId !== this.requestId || searchTerm !== this.searchTerm) {
      searchDeferred?.resolve({ totalCount: 0 });
      return;
    }

    const wrapper = document.createElement('div');
    wrapper.id = 'predictive-search-results';
    wrapper.setAttribute('role', 'listbox');
    const group = document.createElement('div');
    group.id = 'predictive-search-results-groups-wrapper';
    group.className = 'predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-suggestions';
    const resultGroup = document.createElement('div');
    resultGroup.className = 'predictive-search__result-group';
    const heading = document.createElement('h2');
    heading.id = 'predictive-search-products';
    heading.className = 'predictive-search__heading text-body caption-with-letter-spacing';
    heading.textContent = failed ? 'Verificá todos los resultados' : results.length ? 'Productos' : 'Sin coincidencias';
    resultGroup.append(heading);
    const list = document.createElement('ul');
    list.id = 'predictive-search-results-products-list';
    list.className = 'predictive-search__results-list list-unstyled';
    list.setAttribute('role', 'group');
    list.setAttribute('aria-labelledby', heading.id);
    results.slice(0, 8).forEach((item, index) => {
      const copy = document.importNode(item, true);
      copy.id = `predictive-search-option-product-${index + 1}`;
      list.append(copy);
    });
    resultGroup.append(list);
    group.append(resultGroup);
    wrapper.append(group);

    const action = document.createElement('div');
    action.id = 'predictive-search-option-search-keywords';
    action.className = 'predictive-search__search-for-button';
    const button = document.createElement('button');
    button.type = 'submit';
    button.className = 'predictive-search__item predictive-search__item--term link link--text h5';
    const label = document.createElement('span');
    label.dataset.predictiveSearchSearchForText = '';
    label.textContent = 'Ver todos los resultados';
    button.append(label);
    action.append(button);
    wrapper.append(action);

    const status = document.createElement('div');
    status.dataset.predictiveSearchLiveRegionCountValue = '';
    status.dataset.totalResults = String(results.length);
    status.className = 'visually-hidden';
    status.textContent = failed ? 'Buscá en todos los productos' : `${results.length} productos encontrados`;
    wrapper.append(status);

    const markup = wrapper.outerHTML;
    if (!failed) {
      this.allPredictiveSearchInstances.forEach((instance) => {
        instance.cachedResults[queryKey] = markup;
      });
    }
    this.renderSearchResults(markup);
    searchDeferred?.resolve({ totalCount: results.length });
  }

  getTotalResultCount() {
    return parseInt(this.predictiveSearchResults.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
  }

  dispatchSearchUpdateEvent(query) {
    const { SearchUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent) return null;

    const deferred = SearchUpdateEvent.createPromise();
    this.dispatchEvent(
      new SearchUpdateEvent({
        search: { query },
        promise: deferred.promise,
      })
    );
    return deferred;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
